import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Link,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { fetchWithError } from "../utils/fetchWithError.ts";
import { useSnackbar } from 'notistack';
import tintoaLogoWhite from "../img/logo_tintoa_white.png";
import tintoaLogoBlack from "../img/logo_tintoa_black.png";
import { AuthPageShell } from "../components/auth/AuthPageShell.tsx";

export const SendPasswordResetScreen = () => {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isDarkTheme = theme.palette.mode === 'dark';
    const { enqueueSnackbar } = useSnackbar();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const baseUrl = "/auth/send-password-reset";
            const url = new URL(baseUrl, window.location.origin);
            await fetchWithError(url.toString(), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email }),
            });
            enqueueSnackbar("Wenn ein Konto mit dieser E-Mail-Adresse existiert, haben wir dir eine E-Mail mit weiteren Anweisungen gesendet.", { variant: 'success' });
            navigate("/app/login");
        } catch (error) {
            if (error instanceof Error) {
                enqueueSnackbar(error.message, { variant: 'error' });
            } else {
                enqueueSnackbar("Ein Fehler ist aufgetreten. Bitte versuche es später erneut.", { variant: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthPageShell
            isDarkTheme={isDarkTheme}
            desktopSidePanel={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        color: isDarkTheme ? "white" : "text.primary",
                        textAlign: "center",
                        px: 8,
                        py: 4,
                        transition: "all 0.4s ease-in-out"
                    }}
                >
                    <img
                        src={isDarkTheme ? tintoaLogoWhite : tintoaLogoBlack}
                        alt="tintoa-logo"
                        style={{
                            height: 48,
                            marginBottom: 32
                        }}
                    />
                    <Typography
                        variant="h2"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            mb: 2
                        }}
                    >
                        Passwort vergessen?
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            opacity: 0.9,
                            maxWidth: 400,
                            lineHeight: 1.6
                        }}
                    >
                        Kein Problem! Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.
                    </Typography>
                </Box>
            }
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: { xs: 600, lg: "100%" },
                    mx: "auto",
                    display: "flex",
                    flexDirection: "column",
                    flex: { lg: 1 },
                    minHeight: { lg: 0 },
                    height: { lg: "100%" },
                    transition: "all 0.3s ease-in-out"
                }}
            >
                    <Paper
                        elevation={0}
                        sx={{
                            height: isLargeScreen ? '100%' : 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            p: { xs: 3, sm: 4, lg: 5 },
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: isLargeScreen ? 0 : 2,
                            bgcolor: theme.palette.authCard!,
                            backdropFilter: 'blur(25px)',
                            boxShadow: 'none',
                            transition: 'all 0.3s ease-in-out'
                        }}
                    >
                        <Stack 
                            spacing={4} 
                            sx={{ 
                                flex: isLargeScreen ? 1 : 'none',
                                justifyContent: isLargeScreen ? 'center' : 'flex-start'
                            }}
                        >
                            {/* Logo and title for small screens */}
                            {!isLargeScreen && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <img
                                        src={isDarkTheme ? tintoaLogoWhite : tintoaLogoBlack}
                                        alt="tintoa-logo"
                                        style={{ 
                                            height: 32,
                                            marginBottom: 24
                                        }}
                                    />
                                    <Typography 
                                        variant="h4" 
                                        component="h1" 
                                        gutterBottom
                                        sx={{ color: 'text.primary' }}
                                    >
                                        Passwort zurücksetzen
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                            opacity: isDarkTheme ? 1 : 0.7
                                        }}
                                    >
                                        Gib deine E-Mail-Adresse ein, um fortzufahren.
                                    </Typography>
                                </Box>
                            )}

                            {/* Title for large screens */}
                            {isLargeScreen && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography 
                                        variant="h4" 
                                        component="h1" 
                                        gutterBottom
                                        sx={{ color: 'text.primary' }}
                                    >
                                        Passwort zurücksetzen
                                    </Typography>
                                    <Typography 
                                        sx={{ 
                                            color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                            opacity: isDarkTheme ? 1 : 0.7
                                        }}
                                    >
                                        Gib deine E-Mail-Adresse ein
                                    </Typography>
                                </Box>
                            )}

                            <form onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    <TextField
                                        label="E-Mail"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        fullWidth
                                        autoComplete="email"
                                        autoFocus
                                        size={isLargeScreen ? "medium" : "small"}
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size={isLargeScreen ? "large" : "medium"}
                                        disabled={isLoading}
                                        fullWidth
                                        sx={{
                                            py: isLargeScreen ? 1.5 : 1,
                                            fontSize: isLargeScreen ? '1.1rem' : '1rem'
                                        }}
                                    >
                                        {isLoading ? "Wird gesendet..." : "Link zum Zurücksetzen anfordern"}
                                    </Button>
                                </Stack>
                            </form>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: isDarkTheme ? 'text.secondary' : 'text.primary',
                                        opacity: isDarkTheme ? 1 : 0.7
                                    }}
                                >
                                    Zurück zur{" "}
                                    <Link 
                                        component="button"
                                        type="button"
                                        onClick={() => navigate('/app/login')}
                                        underline="hover"
                                        sx={{ 
                                            color: 'primary.main',
                                            fontWeight: 500,
                                            border: 'none',
                                            background: 'none',
                                            p: 0,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Anmeldung
                                    </Link>
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>
        </AuthPageShell>
    );
};